import { useAuthStore } from '../stores/auth';

export const CONSULT_EVENT = 'author-nation-2026';
export const CONSULT_VIA = 'author-nation';
export const CONSULT_TZ = 'America/Los_Angeles';

export interface ConsultDay {
  date: string;
  start: string;
  end: string;
}

export interface ConsultTime {
  startsAt: string;
  open: number;
  date: string;
}

export interface ConsultBooking {
  id: string;
  event: string;
  status: 'booked' | 'checked_in' | 'walk_in' | 'no_show' | 'cancelled';
  startsAt: string;
  durationMin: number;
  label: string;
  location: string;
  address: string;
  projectId: string | null;
  token: string;
  manageUrl: string;
  icsUrl: string;
}

export interface ConsultMe {
  eligible: boolean;
  projectId: string | null;
  booking: ConsultBooking | null;
}

export class ConsultApiError extends Error {
  constructor(
    message: string,
    public code?: string,
    public status?: number
  ) {
    super(message);
  }
}

async function request<T>(url: string, init: RequestInit = {}, auth = true): Promise<T> {
  const headers = new Headers(init.headers);
  if (auth) {
    const authStore = useAuthStore();
    if (authStore.isAuthenticated) {
      for (const [key, value] of Object.entries(await authStore.getAuthHeaders())) headers.set(key, value);
    }
  }
  if (init.body) headers.set('Content-Type', 'application/json');
  const res = await fetch(url, { ...init, headers });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string; code?: string };
  if (!res.ok) throw new ConsultApiError(data.error || 'Something broke. Try again in a sec.', data.code, res.status);
  return data;
}

export const consultApi = {
  times: (event = CONSULT_EVENT) =>
    request<{ event: { name: string; location: string; address: string; days: ConsultDay[] }; times: ConsultTime[] }>(
      `/api/consults/${event}`,
      {},
      false
    ),
  me: (projectId?: string, event = CONSULT_EVENT) =>
    request<ConsultMe>(`/api/consults/${event}/me${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''}`),
  book: (startsAt: string, projectId?: string | null, event = CONSULT_EVENT) =>
    request<{ booking: ConsultBooking }>(`/api/consults/${event}/book`, {
      method: 'POST',
      body: JSON.stringify({ startsAt, projectId: projectId || undefined }),
    }),
  get: (id: string, token?: string) =>
    request<{ booking: ConsultBooking }>(
      `/api/consults/bookings/${id}${token ? `?token=${encodeURIComponent(token)}` : ''}`
    ),
  cancel: (id: string, token?: string) =>
    request<{ booking: ConsultBooking }>(`/api/consults/bookings/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  reschedule: (id: string, startsAt: string, token?: string) =>
    request<{ booking: ConsultBooking }>(`/api/consults/bookings/${id}/reschedule`, {
      method: 'POST',
      body: JSON.stringify({ token, startsAt }),
    }),
};

const fmt = (options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat('en-US', { timeZone: CONSULT_TZ, ...options });

/** "10:20 AM" in Las Vegas time */
export const timeLabel = (iso: string) => fmt({ hour: 'numeric', minute: '2-digit' }).format(new Date(iso));
/** "10 AM" */
export const hourLabel = (iso: string) => fmt({ hour: 'numeric' }).format(new Date(iso));
/** "Tue Nov 10" for an event-local YYYY-MM-DD */
export const dayLabel = (date: string) =>
  new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' })
    .format(new Date(`${date}T12:00:00Z`))
    .replace(',', '');
/** "Wednesday, Nov 11" */
export const longDayLabel = (iso: string) =>
  fmt({ weekday: 'long', month: 'short', day: 'numeric' }).format(new Date(iso));
/** "Wed Nov 11 · 2:10 PM" */
export const shortSlotLabel = (iso: string) =>
  `${fmt({ weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(iso)).replace(',', '')} · ${timeLabel(iso)}`;
